<template>
  <div class="route-agencies-list">
    <v-card v-if="agencies.length === 0" class="text-center pa-6">
      <v-icon size="48" color="grey">mdi-map-marker-off</v-icon>
      <h3 class="text-h6 mt-3 mb-2">No hay agencias asignadas</h3>
      <p class="text-subtitle-2 text-grey">Contacta con tu supervisor para obtener una ruta.</p>
    </v-card>

    <v-row v-else>
      <v-col 
        v-for="(agency, index) in agencies" 
        :key="agency.id"
        cols="12"
        sm="6"
        lg="4"
      >
        <v-card
          :class="getAgencyCardClass(agency.status)"
          :color="getAgencyColor(agency.status)"
          :variant="agency.status === 'pending' ? 'outlined' : 'flat'"
          class="agency-card"
          elevation="2"
        >
          <v-card-title class="d-flex align-center">
            <v-icon :icon="getAgencyIcon(agency.status)" class="mr-3"></v-icon>
            <div class="flex-grow-1">
              <h4 class="text-subtitle-1">{{ agency.name }}</h4>
              <p class="text-caption mb-0 opacity-80">{{ agency.address }}</p>
            </div>
            <v-chip 
              :color="getStatusColor(agency.status)"
              size="small"
              class="text-white"
            >
              {{ getStatusText(agency.status) }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <v-row align="center" class="mb-2">
              <v-col cols="6">
                <v-list-item density="compact" class="px-0">
                  <template #prepend>
                    <v-icon icon="mdi-clock" size="small"></v-icon>
                  </template>
                  <v-list-item-title class="text-caption">
                    {{ formatTime(agency.estimatedTime) }}
                  </v-list-item-title>
                </v-list-item>
              </v-col>
              <v-col cols="6">
                <v-list-item density="compact" class="px-0">
                  <template #prepend>
                    <v-icon icon="mdi-map-marker-distance" size="small"></v-icon>
                  </template>
                  <v-list-item-title class="text-caption">
                    {{ formatDistance(agency.distance) }}
                  </v-list-item-title>
                </v-list-item>
              </v-col>
            </v-row>

            <!-- Información adicional según el estado -->
            <div v-if="agency.status === 'in_progress'" class="mb-3">
              <v-alert color="info" variant="tonal" density="compact">
                <v-icon icon="mdi-clock-time-four" start></v-icon>
                Visita iniciada: {{ formatDateTime(agency.startTime) }}
              </v-alert>
            </div>

            <div v-if="agency.status === 'completed'" class="mb-3">
              <v-alert color="success" variant="tonal" density="compact">
                <v-icon icon="mdi-check-circle" start></v-icon>
                Completada: {{ formatDateTime(agency.completedTime) }}
              </v-alert>
            </div>

            <!-- Botones de acción -->
            <v-row class="mt-3">
              <v-col cols="6">
                <v-btn
                  v-if="agency.status === 'pending'"
                  @click="$emit('updateStatus', agency.id, 'in_progress')"
                  color="success"
                  size="small"
                  block
                  variant="outlined"
                >
                  <v-icon start size="small">mdi-play</v-icon>
                  Iniciar
                </v-btn>

                <v-btn
                  v-else-if="agency.status === 'in_progress'"
                  @click="$emit('updateStatus', agency.id, 'completed')"
                  color="primary"
                  size="small"
                  block
                >
                  <v-icon start size="small">mdi-check</v-icon>
                  Completar
                </v-btn>

                <v-btn
                  v-else
                  color="grey"
                  size="small"
                  block
                  disabled
                >
                  <v-icon start size="small">mdi-check-circle</v-icon>
                  Finalizada
                </v-btn>
              </v-col>
              
              <v-col cols="6">
                <v-btn
                  @click="$emit('reportIncident', agency)"
                  color="warning"
                  size="small"
                  block
                  variant="outlined"
                >
                  <v-icon start size="small">mdi-alert</v-icon>
                  Incidencia
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Orden en la ruta -->
          <v-card-actions class="pt-0">
            <v-chip
              color="primary"
              variant="text"
              size="small"
              class="text-caption"
            >
              <v-icon start size="x-small">mdi-numeric</v-icon>
              Orden: {{ index + 1 }}
            </v-chip>
            
            <v-spacer></v-spacer>
            
            <v-btn
              icon="mdi-map"
              size="small"
              variant="text"
              @click="openInMaps(agency)"
            ></v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Agency {
  id: number
  name: string
  address: string
  status: 'pending' | 'in_progress' | 'completed'
  estimatedTime?: string
  distance?: number
  startTime?: string
  completedTime?: string
  latitude?: number
  longitude?: number
}

interface Props {
  agencies: Agency[]
  currentLocation?: string
}

defineProps<Props>()

const emit = defineEmits<{
  updateStatus: [agencyId: number, status: string]
  reportIncident: [agency: Agency]
}>()

// Métodos de utilidad
const getAgencyCardClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'completed-agency'
    case 'in_progress':
      return 'in-progress-agency'
    default:
      return 'pending-agency'
  }
}

const getAgencyColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'in_progress':
      return 'info'
    default:
      return 'white'
  }
}

const getAgencyIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return 'mdi-check-circle'
    case 'in_progress':
      return 'mdi-clock-time-four'
    default:
      return 'mdi-office-building'
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'in_progress':
      return 'warning'
    default:
      return 'grey'
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Completada'
    case 'in_progress':
      return 'En Progreso'
    default:
      return 'Pendiente'
  }
}

const formatTime = (time?: string) => {
  if (!time) return 'No estimado'
  return time
}

const formatDistance = (distance?: number) => {
  if (!distance) return 'N/A'
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`
  }
  return `${distance.toFixed(1)}km`
}

const formatDateTime = (datetime?: string) => {
  if (!datetime) return 'N/A'
  return new Date(datetime).toLocaleString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit'
  })
}

const openInMaps = (agency: Agency) => {
  if (agency.latitude && agency.longitude) {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${agency.latitude},${agency.longitude}`
    window.open(url, '_blank')
  } else {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(agency.address)}`
    window.open(url, '_blank')
  }
}
</script>

<style scoped>
.route-agencies-list {
  width: 100%;
}

.agency-card {
  transition: all 0.3s ease;
  height: 100%;
}

.agency-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.pending-agency {
  border: 2px solid #e0e0e0;
}

.in-progress-agency {
  border: 2px solid #2196f3;
  background: linear-gradient(45deg, rgba(33, 150, 243, 0.05), rgba(33, 150, 243, 0.1));
}

.completed-agency {
  border: 2px solid #4caf50;
  background: linear-gradient(45deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.1));
}

:deep(.v-alert) {
  font-size: 0.75rem;
}

:deep(.v-btn) {
  font-size: 0.75rem;
}
</style>
